import useTranslation from 'hooks/useTranslation'
import ArrowBtn from 'components/buttons/ArrowBtn'
import React from 'react'

export type KeyCabinetOverlayPhase = 'open' | 'closed' | 'error'

interface OpenOverlayProps {
  phase: KeyCabinetOverlayPhase
  /** Całkowity czas zwolnienia zamka w ms, null = nieznany (bez odliczania) */
  totalMs: number | null
  remainingMs: number
  purpose?: warehouse.WarehouseActionKeyCabinetGrantPurpose
  keyLabel?: string
  errorMessage?: string
  canOpenAgain: boolean
  onOpenAgain: () => void
  onClose: () => void
}

const RADIUS = 54
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const OpenOverlay: React.FC<OpenOverlayProps> = ({ phase, totalMs, remainingMs, purpose, keyLabel, errorMessage, canOpenAgain, onOpenAgain, onClose }) => {
  const { t } = useTranslation('keyCabinet')
  const hasCountdown = phase === 'open' && totalMs !== null && totalMs > 0
  const progress = hasCountdown ? Math.max(0, Math.min(1, remainingMs / (totalMs as number))) : 0
  const secondsLeft = Math.ceil(remainingMs / 1000)

  const title = phase === 'open'
    ? t('keyCabinetOpen')
    : phase === 'closed'
      ? t('lockLockedAgain')
      : t('openFailed')

  const description = phase === 'open'
    ? t(purpose === 'return' ? 'openDoorAndLeaveKey' : 'openDoorAndCollectKey')
    : phase === 'closed'
      ? t('lockLockedAgainDesc')
      : (errorMessage || t('openFailedDesc'))

  return (
    <div className={`keyCabinet__overlay --${phase}`} role='alertdialog' aria-live='assertive' aria-label={title}>
      <div className='keyCabinet__overlay__content'>
        <div className='keyCabinet__overlay__indicator'>
          {hasCountdown
            ? <>
              <svg viewBox='0 0 120 120' aria-hidden='true'>
                <circle className='track' cx='60' cy='60' r={RADIUS} />
                <circle
                  className='progress'
                  cx='60'
                  cy='60'
                  r={RADIUS}
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={CIRCUMFERENCE * (1 - progress)} />
              </svg>
              <div className='keyCabinet__overlay__seconds'>
                <strong>{secondsLeft}</strong>
                <span>s</span>
              </div>
            </>
            : <div className='keyCabinet__overlay__icon'>
              <i className={`icon icon--${phase === 'error' ? 'problem' : phase === 'open' ? 'padlock-open' : 'padlock-closed'}`} />
            </div>}
        </div>

        <h2 className='--secFont'>{title}</h2>
        {hasCountdown && <p className='keyCabinet__overlay__caption'>{t('untilLockLocks')}</p>}
        <p>{description}</p>

        {phase === 'open' && keyLabel && <div className='keyCabinet__overlay__key'>
          <span>{t('key')}</span>
          <strong>{keyLabel}</strong>
        </div>}

        <div className='keyCabinet__overlay__buttons'>
          {phase !== 'open' && canOpenAgain && <ArrowBtn
            enlarged
            showArrow={false}
            text={t(phase === 'error' ? 'tryAgain' : 'openAgain')}
            action={onOpenAgain} />}
          <ArrowBtn
            enlarged={phase === 'open' || !canOpenAgain}
            showArrow={false}
            isBlankButton={phase !== 'open' && canOpenAgain}
            text={t(phase === 'error' ? 'close' : 'done')}
            action={onClose} />
        </div>
      </div>
    </div>
  )
}

export default OpenOverlay
